import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, DicesIcon, FileTextIcon, FolderSearchIcon, LayoutGridIcon, LightbulbIcon, PencilRulerIcon, ShapesIcon, ShuffleIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
export const PRODUCT_IMAGE = 'https://sb.impultienda.ar/storage/v1/object/public/ebook-images/a900450d-c602-4ef9-ae61-7187344a487a/product-uploads/mcp_afe80cc4-818a-40f0-93c2-c27c0daf7966_1793752201734.webp';
export const heroGuarantees = ['Téléchargement numérique immédiat', 'Paiement unique', 'Sans abonnement', 'Garantie satisfaction 7 jours'];
export const stats = [{
  value: '240',
  label: 'Pages dans les tomes principaux'
}, {
  value: '6',
  label: 'Tomes numériques de yoga'
}, {
  value: '96',
  label: 'Cours prêts minute par minute'
}, {
  value: '5',
  label: 'Bonus inclus'
}, {
  value: '1',
  label: 'Année entière de cours, de septembre à août'
}];
export const differenceStats = [{
  value: '96 cours',
  label: 'Minute par minute, en versions de 60, 45 et 30 minutes'
}, {
  value: '6 tomes numériques',
  label: "De septembre à août, une année entière de cours de yoga"
}, {
  value: 'PDF à imprimer',
  label: 'À utiliser sur tablette ou à poser à côté du tapis'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Les dimanches soir disparaissent',
  text: "Des heures devant l'ordinateur à préparer les cours de la semaine – et ce n'est toujours pas fini."
}, {
  title: 'Des heures à chercher',
  text: "Les séquences trouvées sur internet sont trop simples, trop intenses ou pas adaptées à vos élèves."
}, {
  title: 'Chaque cours se construit de zéro',
  text: 'Accueil, centrage, échauffement, cœur de séance, posture clé, retour au calme, savasana – ce qui devrait prendre des minutes devient un vrai travail.'
}, {
  title: 'Difficile de gérer des niveaux différents',
  text: "Débutants, réguliers et seniors dans la même salle : quelqu'un s'ennuie ou force toujours."
}, {
  title: 'Toujours les mêmes mots',
  text: "Les mêmes consignes, le même savasana, la même playlist : vous avez l'impression de tourner en rond."
}, {
  title: 'Le stress avant le cours',
  text: 'Quand le temps manque, on improvise et on bricole la séquence au dernier moment, déjà sur le tapis.'
}];
export const afterItems = ["Une année entière de cours de yoga organisée pour les adultes", 'Des cours minute par minute faciles à suivre', 'Des cours de 60, 45 et 30 minutes prêts', '48 postures clés illustrées pas à pas', '3 niveaux pour chaque posture : douce, standard et intense', 'Des respirations guidées pour chaque période', "Des scripts d'ouverture et de savasana prêts à lire", 'Une ambiance musicale et un thème pour chaque semaine', "Une programmation pour toute l'année"];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Les dimanches passent à préparer',
  text: 'Passez-vous une partie du week-end à préparer les cours de la semaine ?'
}, {
  icon: FolderSearchIcon,
  title: 'Vous ne savez plus quelle séquence proposer',
  text: 'Cherchez-vous sans cesse des postures adaptées au niveau de vos élèves ?'
}, {
  icon: LightbulbIcon,
  title: 'Vous voulez des consignes plus claires',
  text: "Aimeriez-vous savoir exactement quoi dire, de l'ouverture du cours jusqu'au savasana ?"
}, {
  icon: PencilRulerIcon,
  title: 'Vous voulez arrêter de tout créer de zéro',
  text: 'Aimeriez-vous que les cours, les respirations et les scripts soient déjà prêts ?'
}, {
  icon: ShuffleIcon,
  title: 'Vos élèves ont besoin de variété',
  text: "Voulez-vous de nouveaux thèmes, de nouvelles postures et de nouvelles ambiances pour garder le groupe motivé ?"
}, {
  icon: LayoutGridIcon,
  title: 'Il vous faut une structure plus claire',
  text: "Voulez-vous tout votre matériel réuni pour trouver vite ce qu'il vous faut ?"
}];

// Spine colours mirror the physical box (blue, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Tome 01',
  level: 'Sept.–Oct.',
  title: 'Fondations et ancrage',
  tagline: "Toute l'année organisée, et des bases solides.",
  intro: "Le premier tome donne la programmation de l'année entière et 16 cours minute par minute pour poser les fondations en septembre et octobre.",
  items: ["Programmation de l'année, mois par mois", "Les 8 semaines en un coup d'œil", "Structure d'un cours de 60, 45 et 30 minutes", 'Un cours, trois niveaux', '16 cours minute par minute', 'Montagne, chien tête en bas, guerriers I et II', 'Arbre, triangle et pont en 3 niveaux', 'Respirations abdominale, complète, ujjayi et carrée', "Scripts d'ouverture et de savasana"],
  outro: 'Entrez dans la salle en sachant exactement quoi faire.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Tome 02',
  level: 'Nov.–Déc.',
  title: 'Force et feu intérieur',
  tagline: 'De la force, sans forcer.',
  intro: 'Le deuxième tome réunit 16 cours pour travailler la force et le gainage en novembre et décembre, toujours en trois niveaux.',
  items: ['16 cours de 60 et 45 minutes', 'Une version 30 min pour chaque cours', 'Planche, chaise et fente haute', 'Bateau, chaturanga et guerrier III', 'Planche latérale et angle latéral étendu', 'Ujjayi, respiration en paliers et cohérence cardiaque', 'Kapalabhati avec ses précautions', "Scripts d'ouverture et de savasana", 'Ambiances musicales et thèmes des 8 semaines'],
  outro: 'Des cours dynamiques, adaptés à chaque niveau.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Tome 03',
  level: 'Janv.–Févr.',
  title: 'Ouverture et souplesse',
  tagline: 'Ouvrir les hanches, la poitrine et le dos, en douceur.',
  intro: "Le troisième tome contient 16 cours pour travailler l'ouverture et la mobilité en janvier et février, avec des versions soutenues par les accessoires.",
  items: ['16 cours de 60 et 45 minutes', 'Fente basse, papillon et préparation au pigeon', 'Aigle, cobra et sauterelle', 'Pince assise et chameau doux', 'Respiration alternée (nadi shodhana)', 'Expiration allongée et respiration costale', 'Versions avec briques, sangles et bolsters', "Scripts d'ouverture et de savasana", 'Ambiances musicales et thèmes des 8 semaines'],
  outro: "L'hiver devient la saison de l'ouverture.",
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Tome 04',
  level: 'Mars–Avril',
  title: 'Flow et énergie du printemps',
  tagline: 'Un souffle, un mouvement.',
  intro: 'Le quatrième tome propose 16 cours de flow pour le printemps : des enchaînements fluides, guidés par la respiration.',
  items: ['16 cours de 60 et 45 minutes', 'Des enchaînements guidés par le souffle', 'Chien tête en haut et guerrier inversé', 'Demi-lune, guirlande et préparation au corbeau', 'Triangle tourné et chaise tournée', 'Le danseur, avec la version au mur', 'Ujjayi en mouvement et nadi shodhana', "Scripts d'ouverture et de savasana", 'Ambiances musicales et thèmes des 8 semaines'],
  outro: 'Des cours fluides, avec un niveau pour chacun.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Tome 05',
  level: 'Mai–Juin',
  title: 'Yoga doux : dos, nuque et détente',
  tagline: 'Un yoga doux, accessible à tous les élèves.',
  intro: 'Le cinquième tome réunit 16 cours de yoga doux pour mai et juin : dos, nuque et épaules, au sol, à la chaise ou au mur.',
  items: ['16 cours de 60 et 45 minutes', 'Des versions à la chaise et au mur', 'Enfant, sphinx et torsion couchée', "Genoux poitrine et fil dans l'aiguille", 'Demi-chien au mur et jambes au mur', 'Bébé heureux', "Respiration de l'abeille et cohérence cardiaque", "Scripts d'ouverture et de savasana", 'Ambiances musicales et thèmes des 8 semaines'],
  outro: 'Accompagner chaque élève, en douceur.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Tome 06',
  level: 'Juil.–Août',
  title: 'Yin, récupération et fin de saison',
  tagline: 'Ralentir, tenir, relâcher.',
  intro: "Le sixième tome contient 16 cours de yin et de récupération pour l'été, des scripts de yoga nidra et le bilan de l'année.",
  items: ['16 cours de 60 et 45 minutes', 'Papillon, chenille et dragon en yin', 'Cygne endormi, lacet et banane', 'Libellule et savasana', 'Respiration rafraîchissante et respiration lunaire', 'Scripts de savasana et de yoga nidra', 'Ambiances musicales et thèmes des 8 semaines', "Cahier de cours et bilan de l'année"],
  outro: "Une fin d'année sereine, et la rentrée déjà prête.",
  note: 'Ce document ne remplace pas un avis médical. En cas de douleur, de grossesse ou de pathologie, demandez à vos élèves de consulter leur médecin.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: "6 tomes numériques pour toute l'année",
  text: "Un pack complet de yoga pour préparer et guider vos cours de septembre à août. Chaque tome contient 40 pages : 16 cours minute par minute, 8 postures clés en 3 niveaux, des respirations, des scripts, des ambiances musicales et des thèmes."
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Cours prêts',
  text: "Fini la page blanche. Des cours minute par minute avec les mots du prof pour commencer tout de suite."
}, {
  icon: FileTextIcon,
  title: 'Fiches à imprimer',
  text: 'Des fiches de cours, une liste à cocher avant le cours et un cahier de suivi des élèves à imprimer.'
}, {
  icon: ShapesIcon,
  title: 'Postures en images',
  text: 'Des postures clés illustrées étape par étape, en trois niveaux : douce, standard et intense.'
}, {
  icon: DicesIcon,
  title: 'Respirations et scripts',
  text: "Des respirations guidées et des scripts d'ouverture et de savasana à lire à voix lente."
}, {
  icon: ClipboardCheckIcon,
  title: 'Ambiances et thèmes',
  text: "Une ambiance musicale et un thème pour chaque semaine, avec une phrase pour ouvrir et une pour conclure."
}, {
  icon: CalendarCheckIcon,
  title: "Programmation de l'année",
  text: 'De septembre à août, chaque bimestre a son thème, ses postures clés et ses respirations.'
}];
// Pages réelles du Tome 01 : https://sb.impultienda.ar/storage/v1/object/public/ebook-images/a900450d-c602-4ef9-ae61-7187344a487a/product-uploads/mcp_d8265f6f-fbcf-41db-8212-8ea88ff93869_1793752204932.webp = p01 (couverture), https://sb.impultienda.ar/storage/v1/object/public/ebook-images/a900450d-c602-4ef9-ae61-7187344a487a/product-uploads/mcp_5ea4d0ef-754d-4259-9270-ca6947517065_1793752207688.webp = p04 (programmation de l'année), https://sb.impultienda.ar/storage/v1/object/public/ebook-images/a900450d-c602-4ef9-ae61-7187344a487a/product-uploads/mcp_c8859b81-9054-47fd-b028-159ee09b928b_1793752211160.webp = p09 (cours semaine 1 A), https://sb.impultienda.ar/storage/v1/object/public/ebook-images/a900450d-c602-4ef9-ae61-7187344a487a/product-uploads/mcp_9b73375b-522c-464a-bb1c-7219ccdc0a81_1793752214184.webp = p25 (posture clé montagne), https://sb.impultienda.ar/storage/v1/object/public/ebook-images/a900450d-c602-4ef9-ae61-7187344a487a/product-uploads/mcp_bb54ed53-e9b7-470c-a5d3-dac41d44afb8_1793752217036.webp = p35 (scripts de savasana).
export const pageSamples: Array<{src: string; label: string; title: string; text: string}> = [
  {
    "src": "https://sb.impultienda.ar/storage/v1/object/public/ebook-images/a900450d-c602-4ef9-ae61-7187344a487a/product-uploads/mcp_d8265f6f-fbcf-41db-8212-8ea88ff93869_1793752204932.webp",
    "label": "Tome 01 · Page 1",
    "title": "La couverture",
    "text": "Septembre–Octobre · Fondations et ancrage, avec la programmation de l'année."
  },
  {
    "src": "https://sb.impultienda.ar/storage/v1/object/public/ebook-images/a900450d-c602-4ef9-ae61-7187344a487a/product-uploads/mcp_5ea4d0ef-754d-4259-9270-ca6947517065_1793752207688.webp",
    "label": "Programmation · Page 4",
    "title": "La programmation de l'année",
    "text": "Chaque mois avec son thème, sa posture clé, son intention et ses événements."
  },
  {
    "src": "https://sb.impultienda.ar/storage/v1/object/public/ebook-images/a900450d-c602-4ef9-ae61-7187344a487a/product-uploads/mcp_c8859b81-9054-47fd-b028-159ee09b928b_1793752211160.webp",
    "label": "Cours · Page 9",
    "title": "Bienvenue sur le tapis",
    "text": "Le cours de 60 minutes minute par minute, avec les postures, la respiration et les mots du prof."
  },
  {
    "src": "https://sb.impultienda.ar/storage/v1/object/public/ebook-images/a900450d-c602-4ef9-ae61-7187344a487a/product-uploads/mcp_9b73375b-522c-464a-bb1c-7219ccdc0a81_1793752214184.webp",
    "label": "Posture clé · Page 25",
    "title": "La montagne (Tadasana)",
    "text": "Les étapes illustrées et les trois niveaux : douce, standard et intense."
  },
  {
    "src": "https://sb.impultienda.ar/storage/v1/object/public/ebook-images/a900450d-c602-4ef9-ae61-7187344a487a/product-uploads/mcp_bb54ed53-e9b7-470c-a5d3-dac41d44afb8_1793752217036.webp",
    "label": "Scripts · Page 35",
    "title": 'Scripts de savasana et de relaxation',
    "text": "Les textes complets à lire à voix lente pour terminer le cours."
  }
];
export const pageTypes = ['Cours prêts', 'Postures en 3 niveaux', 'Respirations et scripts', "Programmation de l'année"];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Adapter chaque posture – femmes enceintes, seniors et élèves avec douleurs',
  text: 'Chaque élève trouve sa version de la posture, quel que soit son profil.',
  items: ["Questions d'accueil et consentement", 'Fiches par profil', '10 postures adaptées par profil', 'Liste pour un cours inclusif'],
  benefit: 'Personne ne reste sur le côté.'
}, {
  title: 'Cours express – formats prêts de 15, 30 et 45 minutes',
  text: "Un cours raccourci au dernier moment ? 30 formats prêts et la méthode pour compresser n'importe quel cours.",
  items: ['La méthode pour raccourcir un cours', '10 cours de 15 minutes', '10 cours de 30 minutes', '10 cours de 45 minutes'],
  benefit: 'Un cours prêt, quelle que soit la durée.'
}, {
  title: 'Accessoires et variantes – briques, sangles, bolsters, chaise et mur',
  text: 'Comment rendre chaque posture plus accessible ou plus profonde avec le bon accessoire.',
  items: ['Un chapitre par accessoire', '8 postures avec et sans accessoire', 'Un cours sur chaise de 30 minutes', 'Le travail au mur'],
  benefit: 'Le bon accessoire pour chaque élève.'
}, {
  title: 'Groupes de niveaux mixtes – 1 posture, 3 niveaux',
  text: 'Débutants et habitués dans le même cours, et chacun progresse à son rythme.',
  items: ['La méthode des trois niveaux', '40 fiches « une posture, trois niveaux »', 'Le langage inclusif', 'Tableau de bord à imprimer'],
  benefit: "Une seule salle, trois niveaux, personne ne s'ennuie."
}, {
  title: "Cours particuliers et en visio – adapter n'importe quel cours",
  text: "Transformez n'importe quel cours collectif en cours particulier, en petit groupe ou en visio.",
  items: ["Fiche d'accueil de l'élève", '8 cours particuliers prêts', '6 cours en visio de 45 minutes', 'Messages prêts à envoyer'],
  benefit: 'Votre enseignement, partout où sont vos élèves.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Ouvrez YOGA PRÊT™',
  text: "Téléchargez le pack et ouvrez le tome de la période de l'année."
}, {
  title: 'Choisissez le cours',
  text: "Trouvez la semaine et le thème, et choisissez le cours de 60, 45 ou 30 minutes qui convient à vos élèves."
}, {
  title: 'Préparez-vous en 5 minutes',
  text: 'Lisez le déroulé, la posture clé et les mots du prof, et préparez les accessoires et la musique.'
}, {
  title: 'Imprimez et entrez dans la salle',
  text: 'Imprimez la fiche du cours ou gardez-la sur votre tablette – et commencez avec un plan clair.'
}];
export const audienceItems = ['Donnez des cours de yoga à des adultes en studio, en salle ou en association', "Voulez une année entière de cours organisée", 'Voulez arrêter de préparer chaque cours de zéro', 'Avez des niveaux différents dans la même salle', 'Cherchez des séquences sûres et variées', 'Voulez des scripts de savasana et des ambiances prêts', 'Voulez gagner du temps chaque semaine', 'Voulez vous sentir prêt avant chaque cours'];
export const timeValueTasks = ['Chercher des séquences et des postures', 'Écrire le déroulé des cours', 'Trouver un thème et préparer la musique', 'Adapter les postures aux différents niveaux', "Écrire les scripts d'ouverture et de savasana"];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 tomes numériques – 240 pages',
  detail: "Programmation de l'année, cours, postures clés, respirations, scripts et ambiances"
}, {
  label: '5 bonus sans frais supplémentaires',
  detail: 'Adaptations, cours express, accessoires, niveaux mixtes, cours particuliers et en visio'
}, {
  label: '96 cours de yoga minute par minute',
  detail: 'Versions de 60, 45 et 30 minutes, en 3 niveaux, avec les mots du prof'
}];
export const offerIncludes = ['Téléchargement numérique immédiat', 'Fichiers PDF à conserver', 'Fiches à imprimer', 'Aucun produit physique envoyé'];
export const PRICE = '24,99 €';
export const COMPARE_PRICE = '97 €';

// Only add authentic reviews of YOGA PRÊT. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Comment je reçois le matériel ?',
  a: "YOGA PRÊT™ est livré en numérique. Après l'achat, vous avez accès aux fichiers PDF à télécharger et à conserver. Vous pouvez les utiliser sur ordinateur, tablette ou téléphone et imprimer les fiches dont vous avez besoin."
}, {
  q: 'YOGA PRÊT™ est-il adapté aux cours de yoga en France ?',
  a: "Oui. Le matériel est pensé pour les professeurs de yoga qui enseignent à des adultes en studio, en salle de sport ou club de remise en forme, en association, en cours particulier et en visio : cours d'une heure, de 45 ou de 30 minutes, carte 10 cours, abonnement trimestriel, rentrée de septembre et vacances scolaires. Ce document ne remplace pas un avis médical."
}, {
  q: 'Ça convient aux débutants et aux seniors ?',
  a: "Oui. Chaque posture clé a trois niveaux : douce, standard et intense. Les débutants, les élèves réguliers et les seniors trouvent leur version dans le même cours, avec des variantes à la chaise, au mur ou avec des accessoires."
}, {
  q: 'Je peux vraiment préparer un cours en 5 minutes ?',
  a: "YOGA PRÊT™ est conçu pour accélérer la préparation. Vous ouvrez un tome, choisissez un cours prêt et le parcourez en cinq minutes environ. Le temps peut varier selon le groupe et vos adaptations."
}, {
  q: 'Le matériel suffit pour toute une année ?',
  a: "YOGA PRÊT™ contient 96 cours répartis sur toute l'année, de septembre à août, avec 16 cours par tome. Vous choisissez, combinez et adaptez selon votre planning et vos élèves."
}, {
  q: 'Il y a des fiches prêtes à imprimer ?',
  a: 'Oui. Le pack contient des fiches de cours, des fiches posture, des listes à cocher et un cahier de suivi des élèves à imprimer.'
}, {
  q: 'Les cours sont-ils sûrs pour mes élèves ?',
  a: "Le matériel est pensé pour la sécurité : une version douce dans chaque cours, des questions d'accueil, le consentement au toucher, la différence entre intensité et douleur et les contre-indications des postures qui demandent de la prudence. Ce document ne remplace pas un avis médical : en cas de douleur, de grossesse ou de pathologie, demandez à vos élèves de consulter leur médecin."
}, {
  q: "Je peux l'utiliser avec des niveaux mixtes ?",
  a: 'Oui. Chaque posture clé a 3 niveaux, pour que débutants et habitués pratiquent ensemble dans la même salle.'
}, {
  q: 'Je dois tout imprimer ?',
  a: 'Non. Vous imprimez seulement les pages que vous voulez, et vous pouvez utiliser le reste sur tablette.'
}, {
  q: 'Les cinq bonus sont inclus ?',
  a: `Oui. Les cinq bonus sont inclus dans le pack sans frais supplémentaires – le tout pour ${PRICE}.`
}, {
  q: "C'est un abonnement ?",
  a: 'Non. Vous payez une seule fois et vous avez accès au matériel numérique. Aucun prélèvement récurrent.'
}, {
  q: 'Je peux conserver les fichiers ?',
  a: "Oui. Vous pouvez télécharger et conserver les PDF selon les conditions d'utilisation du produit."
}, {
  q: "Je peux l'utiliser sur téléphone ou tablette ?",
  a: "Oui. Les PDF s'ouvrent sur ordinateur, tablette et téléphone. Pour vérifier la mise en page avant d'imprimer, nous conseillons l'ordinateur ou la tablette."
}, {
  q: 'Y a-t-il une garantie ?',
  a: "Oui. YOGA PRÊT™ a une garantie satisfaction de 7 jours. Si le matériel ne correspond pas à vos attentes, contactez-nous pour demander un remboursement pendant la période de garantie, selon les conditions."
}];
export const footerLinks = ['Conditions de vente', 'Politique de confidentialité', 'Politique de remboursement', 'Contact'];
